import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import NavigationZoho from "./components/zoho/NavigationZoho";
import FooterZoho from "./components/zoho/FooterZoho";
import FloatingCTA from "./components/FloatingCTA";
import AbhinavAgent from "./components/AbhinavAgent";
import WhatsAppPopup from "./components/WhatsAppPopup";
import VisitorBeacon from "./components/VisitorBeacon";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "./components/ui/sonner";
import NoIndexRoute from "./components/NoIndexRoute";
import SiteMirror from "./pages/SiteMirror";
import LearnIndex from "./pages/LearnIndex";
import LearnArticle from "./pages/LearnArticle";
import AnswersLibrary from "./pages/AnswersLibrary";
import ContentEngineAdmin from "./pages/ContentEngineAdmin";
import { MIRROR_ROUTES, isMirrorRoute } from "./data/mirrorRoutes";

const LEARN_ROUTE_PREFIXES = ["/learn", "/answers"];
const isLearnRoute = (p) =>
  p === "/learn" || p === "/answers" || p.startsWith("/learn/");

// Pages - Lazy loaded for code splitting
const Home = lazy(() => import("./pages/zoho/HomeZoho"));
const ProductPageZoho = lazy(() => import("./pages/zoho/ProductPageZoho"));
const SolutionPageZoho = lazy(() => import("./pages/zoho/SolutionPageZoho"));
const PricingZoho = lazy(() => import("./pages/zoho/ZohoStubPages").then((m) => ({ default: m.PricingZoho })));
const SigninZoho = lazy(() => import("./pages/zoho/ZohoStubPages").then((m) => ({ default: m.SigninZoho })));
const CustomersZoho = lazy(() => import("./pages/zoho/ZohoStubPages").then((m) => ({ default: m.CustomersZoho })));
const ResourcesZoho = lazy(() => import("./pages/zoho/ZohoStubPages").then((m) => ({ default: m.ResourcesZoho })));
const PlatformZoho = lazy(() => import("./pages/zoho/ZohoStubPages").then((m) => ({ default: m.PlatformZoho })));
const SolutionsHubZoho = lazy(() => import("./pages/zoho/ZohoStubPages").then((m) => ({ default: m.SolutionsHubZoho })));
const AIRoadmapPage = lazy(() => import("./pages/zoho/IntakePages").then((m) => ({ default: m.AIRoadmapPage })));
const CareersPage = lazy(() => import("./pages/zoho/IntakePages").then((m) => ({ default: m.CareersPage })));
const PartnersPage = lazy(() => import("./pages/zoho/IntakePages").then((m) => ({ default: m.PartnersPage })));
const ResponsibleAIPage = lazy(() => import("./pages/zoho/IntakePages").then((m) => ({ default: m.ResponsibleAIPage })));
const HomeV3Legacy = lazy(() => import("./pages/enterprise-v3/HomeV3"));
const RetailAI = lazy(() => import("./pages/enterprise-v3/RetailAI"));
const MallAI = lazy(() => import("./pages/enterprise-v3/MallAI"));
const SolutionPage = lazy(() => import("./pages/enterprise-v3/SolutionPage"));
const PublicInvestorDeck = lazy(() => import("./pages/enterprise-v3/InvestorDeck"));
const FundleBrainV3 = lazy(() => import("./pages/enterprise-v3/FundleBrain"));
const EnterpriseAIAgentsV3 = lazy(() => import("./pages/enterprise-v3/EnterpriseAIAgents"));
const PlatformV3 = lazy(() => import("./pages/enterprise-v3/PlatformV3"));
const IndustryV3 = lazy(() => import("./pages/enterprise-v3/IndustryV3"));
const IndustryV3Hub = lazy(() => import("./pages/enterprise-v3/IndustryV3").then((m) => ({ default: m.IndustryV3Hub })));
const HomeClassic = lazy(() => import("./pages/enterprise/EnterpriseHome"));
const HomeLegacy = lazy(() => import("./pages/Home"));
const Platform = lazy(() => import("./pages/enterprise/EnterprisePlatform"));
const PlatformLegacy = lazy(() => import("./pages/Platform"));
const SuitePage = lazy(() => import("./pages/enterprise/SuitePage"));

// Product Pages
const ADSR = lazy(() => import("./pages/products/ADSR"));
const Loyalty = lazy(() => import("./pages/products/Loyalty"));
const Experiences = lazy(() => import("./pages/products/Experiences"));
const Reach = lazy(() => import("./pages/products/Reach"));
const One = lazy(() => import("./pages/products/One"));
const ECommerce = lazy(() => import("./pages/products/ECommerce"));
const Brain = lazy(() => import("./pages/products/Brain"));
const ConsentFirst = lazy(() => import("./pages/products/ConsentFirst"));
const PillarPage = lazy(() => import("./pages/PillarPage"));
const AudiencePage = lazy(() => import("./pages/AudiencePage"));

// Solution Pages
const Malls = lazy(() => import("./pages/solutions/Malls"));
const Brands = lazy(() => import("./pages/solutions/Brands"));
const Airports = lazy(() => import("./pages/solutions/Airports"));
const TechParks = lazy(() => import("./pages/solutions/TechParks"));
const Hotels = lazy(() => import("./pages/solutions/Hotels"));
const WomensFashion = lazy(() => import("./pages/solutions/WomensFashion"));
const HealthWellness = lazy(() => import("./pages/solutions/HealthWellness"));
const FnBChains = lazy(() => import("./pages/solutions/FnBChains"));

// Utility Pages
const Monetisation = lazy(() => import("./pages/Monetisation"));
const Integrations = lazy(() => import("./pages/Integrations"));
const Security = lazy(() => import("./pages/Security"));
const Contact = lazy(() => import("./pages/Contact"));
const Leads = lazy(() => import("./pages/Leads"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

// Blog / SEO Content Pages
const RetailLoyaltyPlatform = lazy(() => import("./pages/blog/RetailLoyaltyPlatform"));
const MallLoyaltyProgram = lazy(() => import("./pages/blog/MallLoyaltyProgram"));
const AILoyaltyManagement = lazy(() => import("./pages/blog/AILoyaltyManagement"));
const RetailAnalyticsPlatform = lazy(() => import("./pages/blog/RetailAnalyticsPlatform"));
const MobileLoyaltyApp = lazy(() => import("./pages/blog/MobileLoyaltyApp"));
const CRMRetailBrands = lazy(() => import("./pages/blog/CRMRetailBrands"));
const FirstPartyDataRetail = lazy(() => import("./pages/blog/FirstPartyDataRetail"));
const FootfallAnalytics = lazy(() => import("./pages/blog/FootfallAnalytics"));
const CustomerSegmentationRetail = lazy(() => import("./pages/blog/CustomerSegmentationRetail"));
const LoyaltyProgramROI = lazy(() => import("./pages/blog/LoyaltyProgramROI"));
const MallTenantManagement = lazy(() => import("./pages/blog/MallTenantManagement"));
const RetailDataMonetisation = lazy(() => import("./pages/blog/RetailDataMonetisation"));
const DwellTimeAnalytics = lazy(() => import("./pages/blog/DwellTimeAnalytics"));
const HeatMappingRetail = lazy(() => import("./pages/blog/HeatMappingRetail"));
const CustomerRetentionStrategy = lazy(() => import("./pages/blog/CustomerRetentionStrategy"));
const OmnichannelLoyalty = lazy(() => import("./pages/blog/OmnichannelLoyalty"));
const GamificationRetailLoyalty = lazy(() => import("./pages/blog/GamificationRetailLoyalty"));
const GuidePage = lazy(() => import("./pages/GuidePage"));
const LoyaltyMarketplaceGuide = lazy(() => import("./pages/LoyaltyMarketplaceGuide"));
const HeatMapRetailGuide = lazy(() => import("./pages/HeatMapRetailGuide"));
const AiLoyaltySoftwareIndiaGuide = lazy(() => import("./pages/AiLoyaltySoftwareIndiaGuide"));
const AgenticAiLoyaltyGuide = lazy(() => import("./pages/AgenticAiLoyaltyGuide"));
const ScalableLoyaltyCrmGuide = lazy(() => import("./pages/ScalableLoyaltyCrmGuide"));
const Resources = lazy(() => import("./pages/Resources"));
const PillarPageRouter = lazy(() => import("./pages/PillarPageRouter"));
const KnowledgeArticle = lazy(() => import("./pages/KnowledgeArticle"));
const KnowledgeIndex = lazy(() => import("./pages/KnowledgeIndex"));
// CanonicalRoute retired Feb 2026 — all short-URL variants are now
// consolidated via <Navigate> per the Wave 3 SEO audit. The component
// file remains at /pages/CanonicalRoute.jsx for reference.
// const CanonicalRoute = lazy(() => import("./pages/CanonicalRoute"));
const SitemapHtml = lazy(() => import("./pages/SitemapHtml"));
const News = lazy(() => import("./pages/News"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const AdminNews = lazy(() => import("./pages/admin/AdminNews"));
const Downloads = lazy(() => import("./pages/Downloads"));
const ROICalculator = lazy(() => import("./pages/ROICalculator"));
const Industries = lazy(() => import("./pages/Industries"));
const AdminArticles = lazy(() => import("./pages/admin/AdminArticles"));
const AdminLinkedIn = lazy(() => import("./pages/admin/AdminLinkedIn"));
const AdminAgentConversations = lazy(() => import("./pages/admin/AdminAgentConversations"));
const AdminPageRepository = lazy(() => import("./pages/admin/AdminPageRepository"));
const AdminSeoCommandCenter = lazy(() => import("./pages/admin/AdminSeoCommandCenter"));
const InvestorDeck = lazy(() => import("./pages/admin/investor/InvestorDeck"));
const BrainFilm = lazy(() => import("./pages/Brain"));
const RulesEngine = lazy(() => import("./pages/RulesEngine"));
const Customers = lazy(() => import("./pages/Customers"));
const CustomerStoryPage = lazy(() => import("./pages/CustomerStoryPage"));
const NewProductPage = lazy(() => import("./pages/NewProductPage"));

// Loyalty SEO Engine — 46,600 pages
const LoyaltyHub = lazy(() => import("./pages/loyalty/LoyaltyHub"));
const LoyaltyPillarPage = lazy(() => import("./pages/loyalty/LoyaltyPillarPage"));
const CustomerEngagementPlatformPillar = lazy(() => import("./pages/loyalty/CustomerEngagementPlatformPillar"));
const LoyaltyPillarsIndex = lazy(() => import("./pages/loyalty/LoyaltyPillarsIndex"));
const LoyaltyComparisonsIndex = lazy(() => import("./pages/loyalty/LoyaltyComparisonsIndex"));
// SEO REPAIR (Feb 2026): 40K+ thin programmatic city/industry/use-case/glossary
// combos caused GSC to reject ~42K pages. Those routes now render NoIndexRoute
// (imported at the top). Removed lazy imports for those page components.
const LoyaltyComparisonPage = lazy(() => import("./pages/loyalty/LoyaltyComparisonPage"));
const ReportLandingPage = lazy(() => import("./pages/ReportLandingPage"));
const MigrationHub = lazy(() => import("./pages/MigrationHub"));
const BookDemo = lazy(() => import("./pages/BookDemo"));
const About = lazy(() => import("./pages/About"));
// AwardsPress retired Feb 2026 — the page shipped fabricated awards, press
// mentions and speaking engagements. On user instruction the route is now
// 301-redirected to /about. Reintroduce only when real, sourced coverage exists.
// const AwardsPress = lazy(() => import("./pages/AwardsPress"));
const Releases = lazy(() => import("./pages/Releases"));
const LoyaltyFeaturePage = lazy(() => import("./pages/products/LoyaltyFeaturePage"));
const BrandsHub = lazy(() => import("./pages/BrandsHub"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const AIAgents = lazy(() => import("./pages/AIAgents"));
const AIAgentPage = lazy(() => import("./pages/AIAgentPage"));
const PlatformModulePage = lazy(() => import("./pages/PlatformModulePage"));
const UseCases = lazy(() => import("./pages/UseCases"));
const UseCasePage = lazy(() => import("./pages/UseCasePage"));
const IntegrationPage = lazy(() => import("./pages/IntegrationPage"));
const IndustryEnterprisePage = lazy(() => import("./pages/IndustryEnterprisePage"));
import ExitIntentPopup from "./components/ExitIntentPopup";
import ResourceCompass from "./components/ResourceCompass";
import IndustryReportPopup from "./components/IndustryReportPopup";

const PageLoader = () => (
  <div className="min-h-screen bg-slate-950 flex items-center justify-center">
    <div className="w-10 h-10 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
  </div>
);

const SiteChrome = ({ children }) => {
  const { pathname } = useLocation();
  // Mirror (legacy) routes carry their own site-header + footer baked into the
  // static HTML, so suppress React's NavigationZoho/FooterZoho to avoid
  // double-nav. Learn & Answers NOW get the shared Fundle chrome (header +
  // footer + reading layout) — audit fix "give Learn the shared header/footer".
  if (isMirrorRoute(pathname)) return null;
  if (
    pathname.startsWith("/admin/investor") ||
    pathname.startsWith("/brain") ||
    pathname === "/investors" ||
    pathname.startsWith("/investors/")
  ) return null;
  return children;
};

// Marketing popups (industry report + exit intent) must NOT appear on the
// clean enterprise v3 routes ('/', '/retail-ai', '/mall-ai') because they
// carry legacy customer counts / brand logos that violate the Phase 25
// 'no fake metrics' rebrand rule. Also hide on admin/leads routes.
const MarketingPopups = ({ children }) => {
  const { pathname } = useLocation();
  // Marketing popups must stay off every mirror route — the delivered site
  // has no marketing popups and we must not inject new ones over it.
  if (isMirrorRoute(pathname) || isLearnRoute(pathname)) return null;
  const isEnterpriseV3 =
    pathname === "/" ||
    pathname === "/retail-ai" ||
    pathname === "/mall-ai" ||
    pathname.startsWith("/retail-ai/") ||
    pathname.startsWith("/mall-ai/") ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/leads") ||
    pathname.startsWith("/ai-roadmap") ||
    pathname === "/investors" ||
    pathname.startsWith("/investors/") ||
    pathname === "/fundle-brain" ||
    pathname === "/enterprise-ai-agents" ||
    pathname === "/platform";
  if (isEnterpriseV3) return null;
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        {/* Global chrome — Zoho rebrand (Oct 2026): single site-wide
            NavigationZoho + FooterZoho replace the old NavigationV3 +
            FooterV3. Page components no longer render their own nav/footer —
            fixes "double nav / dark inner pages" bug reported by user. */}
        <SiteChrome><NavigationZoho /></SiteChrome>
        <Suspense fallback={<PageLoader />}>
          <main>
            <Routes>
              {/* ===================================================================
                   PRODUCTION MIGRATION (Feb 2026) — Fundle_Final_Website replica.
                   74 reference routes rendered via <SiteMirror /> which fetches the
                   exact delivered HTML from /public/site-mirror/<path>/index.html
                   and injects it in-place (keeps React Router + /admin + /api
                   fully functional). React chrome (Nav/Footer) is hidden on
                   these routes — the delivered HTML has its own header/footer.
                   ================================================================== */}
              {MIRROR_ROUTES.map((r) => (
                <Route key={`mirror-${r}`} path={r} element={<SiteMirror />} />
              ))}

              {/* ===================================================================
                   EDITORIAL ENGINE (Feb 2026) — /learn + /answers.
                   Backed by /public/learn/articles.json (25 complete articles) and
                   /public/learn/question-bank.json (150 answers). See
                   /app/memory/PHASE_28_EDITORIAL.md for the full architecture.
                   ================================================================== */}
              <Route path="/learn" element={<LearnIndex />} />
              <Route path="/learn/:slug" element={<LearnArticle />} />
              <Route path="/answers" element={<AnswersLibrary />} />
              <Route path="/admin/content" element={<ContentEngineAdmin />} />

              {/* Zoho-style redesigned home (Oct 2026). Legacy V3 fallback REMOVED on user instruction ("Keep everything authentic"). */}
              <Route path="/" element={<Home />} />
              <Route path="/retail-ai" element={<RetailAI />} />
              <Route path="/retail-ai/industries" element={<IndustryV3Hub family="retail" />} />
              <Route path="/retail-ai/industries/:slug" element={<IndustryV3 family="retail" />} />
              <Route path="/retail-ai/:slug" element={<SolutionPage platform="retail" />} />
              <Route path="/mall-ai" element={<MallAI />} />
              <Route path="/mall-ai/industries" element={<IndustryV3Hub family="mall" />} />
              <Route path="/mall-ai/industries/:slug" element={<IndustryV3 family="mall" />} />
              <Route path="/mall-ai/:slug" element={<SolutionPage platform="mall" />} />
              <Route path="/investors" element={<PublicInvestorDeck />} />
              <Route path="/fundle-brain" element={<FundleBrainV3 />} />
              <Route path="/enterprise-ai-agents" element={<EnterpriseAIAgentsV3 />} />
              <Route path="/home-classic" element={<HomeClassic />} />
              <Route path="/home-legacy" element={<HomeLegacy />} />
              {/* Platform overview — Zoho-style (Oct 2026). Dark V3 versions removed. */}
              <Route path="/platform" element={<PlatformZoho />} />

              {/* Enterprise AI Suites (Part 2A · Feb 2026 rebrand) */}
              <Route path="/suites" element={<Navigate to="/platform" replace />} />
              <Route path="/suites/:slug" element={<SuitePage />} />
              
              {/* Product Routes */}
              {/* Zoho-style product pages (Sep 2026) — single generic template reading from /src/data/zohoProducts.js.
                  Serves all 6 products: loyalty, brain, experiences, reach, adsr, consentfirst.
                  Legacy per-product components preserved at /legacy/products/:slug for comparison. */}
              <Route path="/products/:slug" element={<ProductPageZoho />} />
              {/* Audit P0 fixes (Oct 2026): rendered-404 destinations in nav/footer that were silently redirecting
                  to homepage. Now serve real, honestly-scoped intake pages — no fake content, no promised features. */}
              <Route path="/ai-roadmap" element={<AIRoadmapPage />} />
              <Route path="/company/careers" element={<CareersPage />} />
              <Route path="/company/partners" element={<PartnersPage />} />
              <Route path="/responsible-ai" element={<ResponsibleAIPage />} />
              <Route path="/products/xperiences" element={<Navigate to="/products/experiences" replace />} />
              {/* Legacy dark V3 product routes removed Oct 2026 per user instruction. */}
              <Route path="/products/one" element={<Navigate to="/platform" replace />} />
              <Route path="/products/ecommerce" element={<Navigate to="/platform" replace />} />

              {/* Strategic Pillar Routes (must come BEFORE /platform/:slug PillarPageRouter) */}
              <Route path="/platform/real-world-activation" element={<PillarPage />} />
              <Route path="/platform/offline-attribution" element={<PillarPage />} />
              <Route path="/platform/partner-ecosystem" element={<PillarPage />} />

              {/* Platform module pages (Phase 1 — static, rank above /platform/:slug) */}
              <Route path="/platform/campaign-automation" element={<PlatformModulePage slug="campaign-automation" />} />
              <Route path="/platform/customer-analytics" element={<PlatformModulePage slug="customer-analytics" />} />
              <Route path="/platform/retail-analytics" element={<PlatformModulePage slug="retail-analytics" />} />
              <Route path="/platform/customer-data-platform" element={<PlatformModulePage slug="customer-data-platform" />} />
              <Route path="/platform/whatsapp-commerce" element={<PlatformModulePage slug="whatsapp-commerce" />} />
              <Route path="/platform/rewards-marketplace" element={<PlatformModulePage slug="rewards-marketplace" />} />

              {/* Feb 2026 additions — Point 4 & 5 new product / platform / solution pages */}
              <Route path="/platform/agentic-seo" element={<NewProductPage slug="agentic-seo" />} />
              <Route path="/platform/social-media-factory" element={<NewProductPage slug="social-media-factory" />} />
              <Route path="/platform/lead-management" element={<NewProductPage slug="lead-management" />} />
              <Route path="/platform/proptech-os" element={<NewProductPage slug="proptech-os" />} />
              <Route path="/platform/whatsapp-mall-loyalty" element={<NewProductPage slug="whatsapp-mall-loyalty" />} />
              <Route path="/platform/mall-app" element={<NewProductPage slug="mall-app" />} />
              <Route path="/platform/mall-microsite" element={<NewProductPage slug="mall-microsite" />} />
              <Route path="/solutions/q-commerce" element={<NewProductPage slug="q-commerce" />} />
              <Route path="/solutions/vending-machines" element={<NewProductPage slug="vending-machines" />} />

              {/* AI Agents (Phase 1 — Agentic AI section) */}
              <Route path="/ai-agents" element={<AIAgents />} />
              <Route path="/ai-agents/:slug" element={<AIAgentPage />} />

              {/* Phase 2 — Use cases + Integration pages */}
              <Route path="/use-cases" element={<UseCases />} />
              <Route path="/use-cases/:slug" element={<UseCasePage />} />
              <Route path="/integrations/:slug" element={<IntegrationPage />} />

              {/* Phase 2 — Industry enterprise pages (static, rank above /industries/:slug) */}
              <Route path="/industries/supermarkets-grocery" element={<IndustryEnterprisePage slug="supermarkets-grocery" />} />
              <Route path="/industries/footwear" element={<IndustryEnterprisePage slug="footwear" />} />
              <Route path="/industries/eyewear-optical" element={<IndustryEnterprisePage slug="eyewear-optical" />} />
              <Route path="/industries/home-furnishings" element={<IndustryEnterprisePage slug="home-furnishings" />} />
              <Route path="/industries/sportswear-athleisure" element={<IndustryEnterprisePage slug="sportswear-athleisure" />} />
              <Route path="/industries/kids-baby-retail" element={<IndustryEnterprisePage slug="kids-baby-retail" />} />
              <Route path="/industries/salons-spas" element={<IndustryEnterprisePage slug="salons-spas" />} />
              <Route path="/industries/petrol-convenience-retail" element={<IndustryEnterprisePage slug="petrol-convenience-retail" />} />
              <Route path="/industries/cinemas-entertainment" element={<IndustryEnterprisePage slug="cinemas-entertainment" />} />
              <Route path="/industries/travel-retail-duty-free" element={<IndustryEnterprisePage slug="travel-retail-duty-free" />} />

              {/* Audience Routes */}
              <Route path="/audience/:slug" element={<AudiencePage />} />
              
              {/* Solution Routes */}
              {/* Industry solutions — all 8 verticals served by SolutionPageZoho from /src/data/mock.js (Oct 2026).
                  Legacy per-industry dark V3 components removed on user instruction. */}
              
              {/* Utility Routes */}
              <Route path="/monetisation" element={<Monetisation />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/security" element={<Security />} />
              <Route path="/pricing" element={<PricingZoho />} />
              <Route path="/signin" element={<SigninZoho />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              
              {/* SEO Content Pages */}
              <Route path="/blog/retail-loyalty-platform" element={<RetailLoyaltyPlatform />} />
              <Route path="/blog/mall-loyalty-program" element={<MallLoyaltyProgram />} />
              <Route path="/blog/ai-loyalty-management" element={<AILoyaltyManagement />} />
              <Route path="/blog/retail-analytics-platform" element={<RetailAnalyticsPlatform />} />
              <Route path="/blog/mobile-loyalty-app" element={<MobileLoyaltyApp />} />
              <Route path="/blog/crm-retail-brands" element={<CRMRetailBrands />} />
              <Route path="/blog/first-party-data-retail" element={<FirstPartyDataRetail />} />
              <Route path="/blog/footfall-analytics" element={<FootfallAnalytics />} />
              <Route path="/blog/customer-segmentation-retail" element={<CustomerSegmentationRetail />} />
              <Route path="/blog/loyalty-program-roi" element={<LoyaltyProgramROI />} />
              <Route path="/blog/mall-tenant-management" element={<MallTenantManagement />} />
              <Route path="/blog/retail-data-monetisation" element={<RetailDataMonetisation />} />
              <Route path="/blog/dwell-time-analytics" element={<DwellTimeAnalytics />} />
              <Route path="/blog/heat-mapping-retail" element={<HeatMappingRetail />} />
              <Route path="/blog/customer-retention-strategy" element={<CustomerRetentionStrategy />} />
              <Route path="/blog/omnichannel-loyalty" element={<OmnichannelLoyalty />} />
              <Route path="/blog/gamification-retail-loyalty" element={<GamificationRetailLoyalty />} />
              <Route path="/resources" element={<ResourcesZoho />} />
              
              {/* Programmatic SEO Pages (500+) */}
              <Route path="/guide/loyalty-marketplace" element={<LoyaltyMarketplaceGuide />} />
              <Route path="/guide/heat-map-retail" element={<HeatMapRetailGuide />} />
              <Route path="/guide/ai-based-loyalty-program-software-india" element={<AiLoyaltySoftwareIndiaGuide />} />
              <Route path="/guide/agentic-ai-loyalty-agents-tailoring-rewards-offers-indian-consumers" element={<AgenticAiLoyaltyGuide />} />
              <Route path="/guide/scalable-loyalty-crm-solutions-expanding-indian-retail-chains" element={<ScalableLoyaltyCrmGuide />} />
              <Route path="/guide/:slug" element={<GuidePage />} />

              {/* Phase 2 — pillar pages (Platform / Solutions / Industries / Compare) */}
              <Route path="/platform/:slug" element={<PillarPageRouter kind="platform" />} />
              <Route path="/solutions-pillar/:slug" element={<PillarPageRouter kind="solutions" />} />
              {/* React Router v6 matches specific routes (e.g. /solutions/malls) before /:slug, so this safely catches all new solution slugs */}
              <Route path="/solutions" element={<SolutionsHubZoho />} />
              <Route path="/solutions/:slug" element={<SolutionPageZoho />} />
              <Route path="/industries/:slug" element={<PillarPageRouter kind="industries" />} />
              <Route path="/compare/:slug" element={<PillarPageRouter kind="compare" />} />

              {/* Phase 3 — Knowledge Hub long-form articles */}
              <Route path="/knowledge" element={<KnowledgeIndex />} />
              <Route path="/knowledge/:slug" element={<KnowledgeArticle />} />

              {/* Phase 4 — Canonical SEO short URLs (user-requested) */}
              {/* Canonical short-URL SEO pages
                  ⚠️ Wave 3 consolidation (Feb 2026 audit): 31 competing variants
                  301-consolidated to a single authority per intent to end
                  keyword cannibalization. `/loyalty-platform` itself is
                  merged into the real product page at `/products/loyalty`. */}
              <Route path="/loyalty-platform" element={<Navigate to="/products/loyalty" replace />} />
              {/* → /products/loyalty (same intent, different keyword permutation) */}
              <Route path="/ai-loyalty-platform" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/customer-loyalty-software" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/retail-loyalty-platform" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/loyalty-crm-platform" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/omnichannel-loyalty-platform" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/rewards-management-platform" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/customer-engagement-platform" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/gamified-loyalty-platform" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/enterprise-loyalty-platform" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/customer-retention-platform" element={<Navigate to="/products/loyalty" replace />} />
              {/* → specific-sibling authority (different intent) */}
              <Route path="/mall-loyalty-platform" element={<Navigate to="/audience/malls" replace />} />
              <Route path="/whatsapp-loyalty-platform" element={<Navigate to="/platform/whatsapp-commerce" replace />} />
              <Route path="/retail-analytics-platform" element={<Navigate to="/platform/retail-analytics" replace />} />
              <Route path="/ai-customer-analytics" element={<Navigate to="/platform/customer-analytics" replace />} />
              <Route path="/retail-media-platform" element={<Navigate to="/products/reach" replace />} />
              <Route path="/first-party-data-platform" element={<Navigate to="/platform/customer-data-platform" replace />} />
              {/* City permutations — all → /products/loyalty (Wave 3 cannibalization fix) */}
              <Route path="/loyalty-platform-mumbai" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/loyalty-platform-delhi" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/loyalty-platform-bangalore" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/loyalty-platform-hyderabad" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/loyalty-platform-chennai" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/loyalty-platform-pune" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/loyalty-platform-kolkata" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/loyalty-platform-ahmedabad" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/loyalty-platform-dubai" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/loyalty-platform-singapore" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/loyalty-platform-london" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/loyalty-platform-new-york" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/loyalty-platform-riyadh" element={<Navigate to="/products/loyalty" replace />} />
              <Route path="/loyalty-platform-abu-dhabi" element={<Navigate to="/products/loyalty" replace />} />
              {/* HTML sitemap (user + crawler-friendly) */}
              <Route path="/sitemap-html" element={<SitemapHtml />} />

              {/* Phase 4 — News aggregator */}
              <Route path="/news" element={<News />} />
              <Route path="/news/:slug" element={<NewsArticle />} />
              <Route path="/admin/news" element={<AdminNews />} />

              {/* Phase 5 — Lead magnets & ROI calculator */}
              <Route path="/downloads" element={<Downloads />} />
              <Route path="/roi-calculator" element={<ROICalculator />} />

              {/* Industries hub + Admin article generator */}
              <Route path="/industries" element={<Industries />} />
              <Route path="/admin/articles" element={<AdminArticles />} />
              <Route path="/admin/linkedin" element={<AdminLinkedIn />} />
              <Route path="/admin/conversations" element={<AdminAgentConversations />} />
              <Route path="/admin/pages" element={<AdminPageRepository />} />
              <Route path="/admin/seo" element={<AdminSeoCommandCenter />} />
              <Route path="/admin/investor" element={<InvestorDeck />} />
              <Route path="/brain" element={<BrainFilm />} />
              <Route path="/rules-engine" element={<RulesEngine />} />

              {/* Wave 1-3 — Antavo-parity pages */}
              <Route path="/customers" element={<CustomersZoho />} />
              <Route path="/customers/:slug" element={<CustomerStoryPage />} />

              {/* Loyalty SEO Engine — REPAIRED Feb 2026: city × industry × use-case combos
                  and thin glossary/example templates were causing 36K "Page with redirect"
                  and 5.9K "crawled not indexed" errors in GSC. Those routes now render a
                  noindex bounce to /loyalty. Real pillar + comparison + vs pages retained. */}
              <Route path="/loyalty" element={<LoyaltyHub />} />
              <Route path="/loyalty/pillars" element={<LoyaltyPillarsIndex />} />
              <Route path="/loyalty/comparisons" element={<LoyaltyComparisonsIndex />} />
              <Route path="/loyalty/pillar/customer-engagement-platform" element={<CustomerEngagementPlatformPillar />} />
              <Route path="/loyalty/pillar/:slug" element={<LoyaltyPillarPage />} />
              <Route path="/loyalty/industry/:slug" element={<NoIndexRoute />} />
              <Route path="/loyalty/example/:slug" element={<NoIndexRoute />} />
              <Route path="/loyalty/glossary/:slug" element={<NoIndexRoute />} />
              <Route path="/loyalty/vs/:slug" element={<LoyaltyComparisonPage />} />
              <Route path="/loyalty/use-case/:slug" element={<NoIndexRoute />} />
              <Route path="/loyalty/in/:city/:industry" element={<NoIndexRoute />} />
              <Route path="/loyalty/ai/:slug" element={<NoIndexRoute />} />
              <Route path="/reports/india-retail-loyalty-report-2026" element={<ReportLandingPage />} />
              <Route path="/migration-hub" element={<MigrationHub />} />
              <Route path="/book-demo" element={<BookDemo />} />
              <Route path="/about" element={<About />} />
              <Route path="/awards-press" element={<Navigate to="/about" replace />} />
              <Route path="/releases" element={<Releases />} />
              <Route path="/products/loyalty/features/:slug" element={<LoyaltyFeaturePage />} />
              <Route path="/brands" element={<BrandsHub />} />
              <Route path="/brands/:slug" element={<BrandPage />} />
              {/* SEO REPAIR (Feb 2026): Catch-all noindex 404 so unrouted URLs
                  don't get indexed as empty pages with default index,follow. */}
              <Route path="*" element={<NoIndexRoute headline="Page not found" body="The page you were looking for isn't here. Head to our knowledge hub." goto="/resources" gotoLabel="Explore Resources" />} />
            </Routes>
          </main>
        </Suspense>
        <SiteChrome>
          <FooterZoho />
        </SiteChrome>
        {/* Live agent + WhatsApp CTAs render on EVERY page (incl. mirror &
            /learn). Each self-suppresses only on /admin and /leads. */}
        <AbhinavAgent />
        <WhatsAppPopup />
        <MarketingPopups>
          <ExitIntentPopup />
          <IndustryReportPopup />
        </MarketingPopups>
        <VisitorBeacon />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
